import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getApiBaseUrl } from '@/config/environment';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CATEGORY_COLORS: Record<string, { bg: string; border: string }> = {
  'self-drive': { bg: 'rgba(76, 175, 80, 0.7)', border: 'rgb(76, 175, 80)' },
  'with-driver': { bg: 'rgba(33, 150, 243, 0.7)', border: 'rgb(33, 150, 243)' },
  'driver-hire': { bg: 'rgba(255, 152, 0, 0.7)', border: 'rgb(255, 152, 0)' },
  'yacht-charter': { bg: 'rgba(156, 39, 176, 0.7)', border: 'rgb(156, 39, 176)' },
  'hybrid': { bg: 'rgba(0, 188, 212, 0.7)', border: 'rgb(0, 188, 212)' },
};

const CATEGORY_LABELS: Record<string, string> = {
  'self-drive': 'Self-Drive',
  'with-driver': 'With Driver',
  'driver-hire': 'Driver Hire',
  'yacht-charter': 'Yacht Charter',
  'hybrid': 'Hybrid',
};

interface TrendDay {
  date: string;
  'self-drive': number;
  'with-driver': number;
  'driver-hire': number;
  'yacht-charter': number;
  hybrid: number;
  total: number;
}

interface BookingTrendsChartProps {
  startDate: string;
  endDate: string;
  height?: number;
}

const BookingTrendsChart: React.FC<BookingTrendsChartProps> = ({
  startDate,
  endDate,
  height = 320,
}) => {
  const theme = useTheme();
  const [trends, setTrends] = useState<TrendDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const fetchTrends = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${getApiBaseUrl()}/api/v1/admin/analytics/booking-trends?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setTrends(result.data.trends);
          }
        }
      } catch (error) {
        console.error('Failed to fetch booking trends:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [startDate, endDate]);

  const categories = Object.keys(CATEGORY_COLORS);

  // For large date ranges, show fewer labels to avoid crowding
  const maxLabels = 15;
  const skipFactor = trends.length > maxLabels ? Math.ceil(trends.length / maxLabels) : 1;

  const labels = trends.map((t, i) => {
    const d = new Date(t.date + 'T00:00:00');
    // For ranges > 30 days, show shorter format
    if (trends.length > 30) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  });

  const datasets = categories.map((cat) => ({
    label: CATEGORY_LABELS[cat] || cat,
    data: trends.map((t) => (t as any)[cat] || 0),
    backgroundColor: CATEGORY_COLORS[cat].bg,
    borderColor: CATEGORY_COLORS[cat].border,
    borderWidth: 1,
    borderRadius: 4,
  }));

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
          usePointStyle: true,
          pointStyle: 'rectRounded' as const,
          padding: 16,
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          footer: (items: any[]) => {
            const total = items.reduce((sum: number, item: any) => sum + item.raw, 0);
            return `Total: ${total}`;
          },
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          color: theme.palette.text.secondary,
          font: { size: 11 },
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: maxLabels,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: theme.palette.divider },
        ticks: {
          color: theme.palette.text.secondary,
          stepSize: 1,
          font: { size: 11 },
        },
      },
    },
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Booking Trends
        </Typography>
        <Box sx={{ height }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : trends.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="text.secondary">No booking data for this period</Typography>
            </Box>
          ) : (
            <Bar options={options} data={{ labels, datasets }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookingTrendsChart;
